import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CategorySidebar from './components/CategorySidebar';
import ArticleList from './components/ArticleList';
import ArticleReader from './components/ArticleReader';
import FilterBar from './components/FilterBar';
import { useAuth } from '../../contexts/AuthContext';
import { HELP_ARTICLES, ARTICLE_CATEGORIES, searchArticles } from '../../data/helpArticles';

const KnowledgeBaseArticles = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState(searchParams?.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterContentType, setFilterContentType] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/authentication');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const articleId = searchParams?.get('article');
    if (articleId) {
      const article = HELP_ARTICLES?.find(a => a?.id === parseInt(articleId));
      if (article) setSelectedArticle(article);
    }
  }, [searchParams]);

  const categories = ARTICLE_CATEGORIES.map(cat => ({
    id: cat.id,
    name: cat.name,
    icon: cat.icon,
    articleCount: cat.articles.length,
    subcategories: [],
  }));

  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedArticle(null);
  };

  const handleArticleSelect = (article) => {
    setSelectedArticle(article);
    setShowMobileMenu(false);
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
  };

  if (authLoading) {
    return (
      <NavigationBar>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </NavigationBar>
    );
  }

  return (
    <NavigationBar>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/help-center-hub')}
                  className="text-muted-foreground hover:text-foreground transition-smooth"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-foreground">
                    Knowledge Base
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Comprehensive documentation and guides
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="Menu"
                className="lg:hidden"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              />
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Icon
                name="Search"
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <Input
                type="search"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => handleSearch(e?.target?.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-6">
            {/* Sidebar */}
            <div className={`lg:block ${showMobileMenu ? 'block' : 'hidden'} w-full lg:w-64 flex-shrink-0`}>
              <CategorySidebar
                categories={categories}
                selectedCategory={selectedCategory}
                onCategorySelect={setSelectedCategory}
              />
            </div>

            {/* Article List or Reader */}
            <div className="flex-1 min-w-0">
              {selectedArticle ? (
                <ArticleReader
                  article={selectedArticle}
                  onBack={handleBackToList}
                  relatedArticles={HELP_ARTICLES?.filter(a => a?.categoryId === selectedArticle?.categoryId && a?.id !== selectedArticle?.id)?.slice(0, 3)}
                />
              ) : (
                <>
                  <FilterBar
                    filterDifficulty={filterDifficulty}
                    filterContentType={filterContentType}
                    sortBy={sortBy}
                    onDifficultyChange={setFilterDifficulty}
                    onContentTypeChange={setFilterContentType}
                    onSortChange={setSortBy}
                  />
                  <ArticleList
                    articles={searchQuery ? searchArticles(searchQuery) : HELP_ARTICLES}
                    searchQuery={searchQuery}
                    selectedCategory={selectedCategory}
                    filterDifficulty={filterDifficulty}
                    filterContentType={filterContentType}
                    sortBy={sortBy}
                    onArticleSelect={handleArticleSelect}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </NavigationBar>
  );
};

export default KnowledgeBaseArticles;